/**
 * KOVA skeleton shimmer.
 *
 * Purpose: replace text "Loading…" with a KOVA-branded skeleton so slow
 * queries feel intentional. Uses the design tokens (`--surface`, `--card`,
 * `--primary`) via CSS variables — no per-page colours.
 *
 * Presets (composable): line, block, kpi, table, page, card.
 */
import React from "react";

/* Primitive block — everything else builds on this. */
export function Shimmer({ className = "", style, testid = "kova-shimmer" }) {
  return (
    <span
      role="presentation"
      aria-hidden="true"
      data-testid={testid}
      style={style}
      className={
        "block rounded-[8px] bg-[#1A1F1C] relative overflow-hidden " +
        "before:absolute before:inset-0 before:-translate-x-full " +
        "before:bg-gradient-to-r before:from-transparent " +
        "before:via-[rgba(57,255,20,0.06)] before:to-transparent " +
        "before:animate-[kova-shimmer_1.6s_ease-in-out_infinite] " +
        className
      }
    />
  );
}

export function SkeletonLine({ w = "100%", h = 12, className = "" }) {
  return <Shimmer style={{ width: w, height: h }} className={className} />;
}

/* KPI-card skeleton — matches Dashboard KPI cards. */
export function SkeletonKPI({ count = 4 }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="skeleton-kpi">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="tzat-kpi">
          <SkeletonLine w={90} h={9} className="mb-4" />
          <SkeletonLine w={140} h={26} />
          <SkeletonLine w={70} h={9} className="mt-3" />
        </div>
      ))}
    </div>
  );
}

/* Row-based table skeleton. */
export function SkeletonTable({ rows = 6, cols = 5 }) {
  return (
    <div className="tzat-card overflow-hidden" data-testid="skeleton-table">
      <div className="px-4 py-3 border-b border-[#3A4640] bg-[#121815] flex gap-3">
        {Array.from({ length: cols }).map((_, i) => (
          <SkeletonLine key={i} w={i === 0 ? 60 : "100%"} h={9} className="flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="px-4 py-3 border-b border-[#3A4640] last:border-0 flex gap-3 items-center">
          {Array.from({ length: cols }).map((_, c) => (
            <SkeletonLine
              key={c}
              w={c === 0 ? 70 : "100%"}
              h={12}
              className={c === 0 ? "" : "flex-1"}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/* Card skeleton — for detail views. */
export function SkeletonCard({ lines = 4 }) {
  return (
    <div className="tzat-card p-6 space-y-3" data-testid="skeleton-card">
      <SkeletonLine w={140} h={18} />
      <SkeletonLine w={80} h={9} />
      <div className="pt-3 space-y-2">
        {Array.from({ length: lines }).map((_, i) => (
          <SkeletonLine key={i} w={`${72 - i * 8}%`} h={11} />
        ))}
      </div>
    </div>
  );
}

/* Full-page loader — pairs a header stripe + KPI grid + table. */
export function SkeletonPage({ title = true, kpi = true, table = true, rows = 8, cols = 6 }) {
  return (
    <div className="p-8 lg:p-10 max-w-[1600px]" data-testid="skeleton-page">
      {title && (
        <div className="mb-8 pb-6 border-b border-[#3A4640]">
          <SkeletonLine w={90} h={9} className="mb-3" />
          <SkeletonLine w={220} h={30} />
          <SkeletonLine w={360} h={11} className="mt-3" />
        </div>
      )}
      {kpi && <div className="mb-6"><SkeletonKPI /></div>}
      {table && <SkeletonTable rows={rows} cols={cols} />}
    </div>
  );
}

/* Small inline label for buttons or in-form loaders — keeps the pulsing
   green identity without using a text spinner. */
export function KovaLoadingPill({ label = "Loading" }) {
  return (
    <span
      className="inline-flex items-center gap-2 text-xs text-[#8A8F98] font-heading uppercase tracking-[0.15em]"
      data-testid="kova-loading-pill"
    >
      <span className="tzat-pulse" /> {label}
    </span>
  );
}

export default SkeletonPage;
